/**
 * Project members component
 * Modified on 16-06-2025
 */

//  Generic imports
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

//  Motif imports
import {
  MotifTable,
  MotifBadge,
  MotifToast,
  MotifButton,
} from "@ey-xd/motif-react";
import MotifModal, {
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
} from "@ey-xd/motif-react/Modal";

//  Icons imports
import iconGroup from "../../../../assets/images/group.svg";
import iconRefresh from "../../../../assets/images/refresh.svg";
import iconCheckCircle from "../../../../assets/images/check-circle.svg";
import iconDefaultUser from "../../../../assets/images/profile_user_icon.jpg";
import iconProgressLoaderCircle from "../../../../assets/images/Loading_indicator_circles.svg";

//  Redux imports
import { setActiveProjectUserRole } from "../../../../redux/userSlice";

//  Internal imports
import "./ProjectMembers.scss";
import GraphService from "../../../../services/graphService.js";
import MemberService from "../../../../services/memberService";

const ProjectMembers = ({ isOpen, setOnclose }) => {
  let [editrowData, setEditrowData] = useState([]);
  const [showMessage, setShowMessage] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const { user, activeProject } = useSelector((state) => state.user);
  const projectId = activeProject?.project_UUID;
  const projectName = activeProject?.projectName;
  const dataHostingLocation = activeProject?.dataHostingLocation;

  const dispatch = useDispatch();

  useEffect(() => {
    fetchMemberDetails();
    // eslint-disable-next-line
  }, []);

  const fetchMemberDetails = async (refreshFlag) => {
    setShowLoader(true);
    let dataReturn = [];
    setEditrowData([]);
    const memberService = new MemberService();
    let data = await memberService.GetProjectDetailById(projectId);
    if (data) {
      dataReturn = data;
      const curUserRole = data.filter(
        (element) => element.email === user.username
      );
      if (curUserRole && curUserRole.length > 0) {
        dispatch(
          setActiveProjectUserRole({
            activeProjectUserRole: curUserRole[0].role,
          })
        );
      }
      const graphService = new GraphService();
      let getMembersAdded = [];
      for (const element of dataReturn) {
        const userbymail = await graphService.getUserDetailsByInput(
          element.email
        );
        let userAvatar = iconDefaultUser;
        if (userbymail) {
          const userAvatarBlob = await graphService.getUserAvatar(
            userbymail.value[0]?.id
          );
          if (userAvatarBlob) {
            const blob = new Blob([userAvatarBlob], { type: "image/jpeg" });
            userAvatar = URL.createObjectURL(blob);
          }
        }
        let newRow = [];
        newRow = {
          FullName: element.fullName,
          Title: element.title,
          Role: element.role,
          Email: element.email,
          FirstName: element.firstName,
          LastName: element.lastName,
          Project_UUID: element.project_UUID,
          ProjectName: projectName,
          Avatar: userAvatar,
          GroupName: element.groupName,
        };
        getMembersAdded.push(newRow);
      }
      setEditrowData((prevState) => [...prevState, ...getMembersAdded]);
      setShowLoader(false);
    }
    if (refreshFlag === true) {
      setShowMessage(true);
    }
  };

  const colDefs = [
    {
      headerName: "Name and email address",
      field: "FullName",
      wrapText: true,
      flex: 2,
      autoHeight: true,
      cellRenderer: (params) => {
        return (
          <PeoplePickerAvatarRenderer
            value={params}
          ></PeoplePickerAvatarRenderer>
        );
      },
    },
    {
      headerName: "Role",
      field: "Role",
      wrapText: true,
      width: 150,
      autoHeight: true,
      colId: "role",
    },
  ];

  const PeoplePickerAvatarRenderer = (value) => {
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [userName, setUserName] = useState(null);
    const [userEmail, setUserEmail] = useState(null);

    useEffect(() => {
      if (value.value.data.isAlreadySaved) {
        setUserName(value.value.data.FullName);
        setUserEmail(value.value.data.Email);

        if (value.value.data.Avatar) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setAvatarUrl(e.target.result);
          };
          reader.readAsDataURL(value.value.data.Avatar);
        } else {
          setAvatarUrl(iconDefaultUser);
        }
      } else {
        if (value.value.data.Avatar) {
          setAvatarUrl(value.value.data.Avatar);
        } else {
          setAvatarUrl(iconDefaultUser);
        }
        setUserName(value.value.data.FullName);
        setUserEmail(value.value.data.Email);
      }
      // eslint-disable-next-line
    }, []);

    return (
      <div className="avtColumn">
        {<img src={avatarUrl} alt="Avatar" className="avtImage"></img>}
        <div className="Namedisplay">
          {userName || ""}
          <div className="Emaildisplay"> {userEmail || ""}</div>
        </div>
      </div>
    );
  };

  const oncloseclick = () => {
    setShowMessage(false);
  };

  const onMemberGridReady = (event) => {
    event.api.sizeColumnsToFit();
  };

  const refreshList = () => {
    fetchMemberDetails(true);
  };

  const platformRedirectClick = () => {
    const base = process.env.REACT_APP_MEMBERS_PLATFORM_URL;
    const url =
      base +
      "?p_id=" +
      projectId +
      "&dataHostingLocation=" +
      dataHostingLocation;
    window.open(url, "_blank");
  };

  return (
    <MotifModal
      show={isOpen}
      onClose={() => setOnclose(!isOpen)}
      className="membersModal modalMotifFooterModal"
    >
      <MotifModalHeader
        className="membersHeader modalNoHeaderBorder"
        closeButtonProps={{
          "aria-label": "Custom Close Button aria-label",
          title: "Custom Close Button title",
        }}
      >
        <div className="memBadge">
          <div className="membersHead">Members</div>
          <MotifBadge
            value={editrowData.length}
            variant="default"
            className="member-badge"
          />
        </div>
      </MotifModalHeader>
      <MotifModalBody className="membersModalBody">
        <React.Fragment key=".0">
          {showLoader ? (
            <div className="loaderDiv">
              <div>
                <img
                  src={iconProgressLoaderCircle}
                  alt="Progress"
                  className="refreshIcon "
                />
              </div>
              <div className="refreshListText">Refreshing List...</div>
            </div>
          ) : (
            <MotifTable
              className="memberTable"
              id="MemberTbl"
              columnDefs={colDefs}
              rowData={editrowData}
              onGridReady={onMemberGridReady}
              suppressCellFocus
              suppressRowClickSelection
              suppressRowHoverHighlight
            />
          )}
        </React.Fragment>
      </MotifModalBody>
      <MotifModalFooter className="member-footer">
        {showMessage && (
          <MotifToast
            className="notifyToast"
            variant="success"
            icons={{ toast: <img src={iconCheckCircle} alt="Success" /> }}
            actionName=""
            position=""
            onClose={() => {
              oncloseclick();
            }}
          >
            <div>Refresh Complete</div>
          </MotifToast>
        )}
        <div className="memberButtons">
          <MotifButton
            className="btnRefreshDocument"
            variant="secondary"
            onClick={() => refreshList()}
          >
            <img src={iconRefresh} alt="Refresh" className="iconRefresh" />
            {/*
             */}
            Refresh
          </MotifButton>
          <MotifButton
            className="btnAccessMember"
            variant="primary"
            onClick={platformRedirectClick}
            disabled={showLoader}
          >
            <img
              src={iconGroup}
              alt="Member"
              className="iconAccessMember"
            />
            <span className="textEllipsis">Access Members List</span>
          </MotifButton>
        </div>
      </MotifModalFooter>
    </MotifModal>
  );
};

ProjectMembers.propTypes = {
  isOpen: PropTypes.bool,
  setOnclose: PropTypes.func,
};

export default ProjectMembers;
